// Client half of the widget rewards panel (Passes loyalty).
//
// All calls go to the loyalty-widget Supabase edge function, authenticated by
// the SAME visitor-session token api/messages uses (see widget-session.ts) —
// the browser never names a serial or member id, the server re-derives both
// from the token's contact. The function itself is deployed --no-verify-jwt
// (like vert); the session token is the credential.

import { getWidgetToken } from "./widget-session";

const LOYALTY_FN_URL =
  "https://zvhwjpeeapujvuudfdps.supabase.co/functions/v1/loyalty-widget";

// ── Config stored on web_chat_widgets.rewards (NULL = feature off) ───────────

export interface RewardsConfig {
  enabled?: boolean;
  /** Home-card title. Default "My rewards". */
  card_title?: string;
  /** Home-card subtitle. Default "Points, rewards and referrals". */
  card_subtitle?: string;
  /** Join CTA heading for non-members. */
  join_title?: string;
  /** Join CTA body copy for non-members. */
  join_subtitle?: string;
  /** Section visibility — all default true. */
  show_referral?: boolean;
  show_wallet?: boolean;
  show_activity?: boolean;
  /** Let identified non-members join from the widget. Default true. */
  join_enabled?: boolean;
}

// ── Server payload shapes (mirror supabase/functions/loyalty-widget) ─────────

export interface RewardsTier {
  name: string;
  min: number;
}

export interface RewardsOption {
  option_index: number;
  points: number;
  kind: "percentage" | "fixed";
  value: number;
  description: string;
  expiry_days: number;
}

export interface RewardsProgram {
  currency: string;
  points_per_currency: number;
  welcome_bonus: number;
  tiers: RewardsTier[];
  redemption_options: RewardsOption[];
  rules: string[];
  referrals: { enabled: true; referrer_points: number | null; referee_bonus: number | null } | null;
}

export interface RewardsLedgerRow {
  delta: number;
  balance_after: number | null;
  reason: string;
  description: string | null;
  created_at: string;
}

export interface RewardsRedemptionRow {
  code: string;
  description: string | null;
  points_spent: number;
  status: string;
  expires_at: string | null;
  created_at: string;
}

export interface RewardsPayload {
  enabled: boolean;
  enrolled?: boolean;
  program?: RewardsProgram;
  member?: {
    full_name: string | null;
    points_balance: number;
    lifetime_points: number;
    orders_count: number;
    tier: string | null;
    enrolled_at: string;
  };
  ledger?: RewardsLedgerRow[];
  redemptions?: RewardsRedemptionRow[];
  install_url?: string;
  dashboard_url?: string;
  referral_url?: string | null;
}

export interface RedeemResult {
  success: boolean;
  code: string | null;
  description: string | null;
  expires_at: string | null;
  points_balance: number | null;
  needed: number | null;
  error: string | null;
}

export interface EnrollResult extends RewardsPayload {
  success: boolean;
  already_member?: boolean;
  welcome_points?: number;
  error?: string;
}

// ── Transport ────────────────────────────────────────────────────────────────

async function post(
  organizationId: string,
  contactId: string,
  body: Record<string, unknown>,
): Promise<any | null> {
  let token = await getWidgetToken(organizationId, contactId);
  if (!token) return null;
  const doFetch = (t: string) =>
    fetch(LOYALTY_FN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, session_token: t }),
    });
  try {
    let res = await doFetch(token);
    if (res.status === 401) {
      token = await getWidgetToken(organizationId, contactId, true);
      if (!token) return null;
      res = await doFetch(token);
    }
    return await res.json().catch(() => null);
  } catch {
    return null;
  }
}

export async function fetchRewards(
  organizationId: string,
  contactId: string,
): Promise<RewardsPayload | null> {
  const out = await post(organizationId, contactId, { action: "member" });
  return out && typeof out.enabled === "boolean" ? (out as RewardsPayload) : null;
}

export async function redeemReward(
  organizationId: string,
  contactId: string,
  optionIndex: number,
): Promise<RedeemResult | null> {
  const out = await post(organizationId, contactId, {
    action: "redeem",
    option_index: optionIndex,
  });
  return out && typeof out.success === "boolean" ? (out as RedeemResult) : null;
}

export async function enrollRewards(
  organizationId: string,
  contactId: string,
): Promise<EnrollResult | null> {
  const out = await post(organizationId, contactId, { action: "enroll" });
  return out && typeof out.success === "boolean" ? (out as EnrollResult) : null;
}
